import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Loader2, Upload, Mail, Send, RefreshCw, Trash2, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin Mailing List page.
 *  - CSV import (Artlogic / Mailchimp / generic)
 *  - Subscriber count + recent list
 *  - One-click triggers for the three campaigns (digest, new arrivals, consign pitch)
 *  - Campaign run history
 */
const SubscribersManager = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [count, setCount] = useState(0);
  const [active, setActive] = useState(0);
  const [history, setHistory] = useState([]);
  const [recipientsForRun, setRecipientsForRun] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [busy, setBusy] = useState({});
  const fileInput = useRef(null);

  // Auth carried by httpOnly cookie — no localStorage token.
  const refresh = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/subscribers?limit=100&include_unsubscribed=false`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load subscribers');
      const j = await res.json();
      setSubscribers(j.subscribers || []);
      setCount(j.count || 0);
      setActive(j.active || 0);
    } catch (e) { toast.error(e.message); }

    try {
      const res = await fetch(`${API_URL}/api/admin/campaigns/history?limit=15`, { credentials: 'include' });
      const j = await res.json();
      setHistory(j.runs || []);
    } catch (err) {
      // Campaign history is non-essential — list still renders without it.
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[subscribers] campaign history fetch failed:', err?.message);
      }
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleImport = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportSummary(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/admin/subscribers/import`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Import failed (${res.status})`);
      }
      const j = await res.json();
      setImportSummary(j);
      toast.success(`Imported ${j.inserted} new subscriber${j.inserted === 1 ? '' : 's'}`);
      await refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setImporting(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const fireCampaign = async (kind, label, opts = {}) => {
    if (!opts.test && !window.confirm(`Send "${label}" to ${active} subscribers now?`)) return;
    setBusy((b) => ({ ...b, [kind]: true }));
    try {
      const url = `${API_URL}/api/admin/campaigns/${kind}${opts.test ? '?test_only=true' : ''}`;
      const res = await fetch(url, { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      const j = await res.json();
      toast.success(`${label} → sent ${j.sent ?? '?'} of ${j.recipients ?? 1}`);
      await refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy((b) => ({ ...b, [kind]: false }));
    }
  };

  const deleteSub = async (id) => {
    if (!window.confirm('Remove this subscriber permanently?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/subscribers/${id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Removed');
      await refresh();
    } catch (e) { toast.error(e.message); }
  };

  const loadRecipients = async (runId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/campaigns/${runId}/recipients?limit=200`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load recipients');
      setRecipientsForRun(await res.json());
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-8" data-testid="subscribers-manager">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif">Mailing list</h1>
          <p className="font-sans text-sm text-muted-foreground mt-1">
            {active.toLocaleString()} active · {count.toLocaleString()} total
          </p>
        </div>
        <button
          onClick={refresh}
          className="px-3 py-2 text-xs uppercase tracking-wider border border-border hover:bg-muted/30 inline-flex items-center gap-2"
          data-testid="subscribers-refresh-btn"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Import card */}
      <section className="border border-border p-6 space-y-4">
        <h2 className="text-sm uppercase tracking-[0.25em] text-muted-foreground font-sans">Import contacts</h2>
        <p className="font-sans text-sm">
          Upload a CSV (or a ZIP containing one or more CSVs). Opt-out flags, deceased contacts and
          duplicates are automatically filtered.
        </p>
        <div className="flex flex-wrap gap-3 items-center">
          <input
            ref={fileInput} type="file" accept=".csv,.zip"
            className="font-sans text-xs" data-testid="subscribers-csv-input"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleImport(f);
            }}
          />
          {importing && <span className="inline-flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Importing…</span>}
        </div>
        {importSummary && (
          <div className="text-xs font-sans text-muted-foreground space-y-1" data-testid="subscribers-import-summary">
            <div>Inserted: {importSummary.inserted}</div>
            <div>Skipped (duplicate): {importSummary.skipped_existing}</div>
            <div>Skipped (opted out): {importSummary.skipped_optout}</div>
            <div>Skipped (deceased): {importSummary.skipped_deceased}</div>
            <div>Skipped (invalid): {importSummary.skipped_invalid}</div>
            {importSummary.files_processed && (
              <div>Files processed: {importSummary.files_processed}</div>
            )}
          </div>
        )}
      </section>

      {/* Campaign triggers */}
      <section className="border border-border p-6 space-y-4">
        <h2 className="text-sm uppercase tracking-[0.25em] text-muted-foreground font-sans">Campaigns</h2>
        <p className="font-sans text-sm text-muted-foreground">
          Two campaigns run automatically — Sunday digest to staff at 9am London, and New Arrivals
          to subscribers Saturday at 10am London. You can also fire any of them manually.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <CampaignCard
            kind="digest"
            label="Internal digest"
            sub="Last 7 days · sent to info@"
            icon={<Users className="w-4 h-4" />}
            busy={busy.digest}
            onFire={() => fireCampaign('digest', 'Internal digest')}
          />
          <CampaignCard
            kind="new-arrivals"
            label="New arrivals"
            sub="Auto-built from last 7 days"
            icon={<Mail className="w-4 h-4" />}
            busy={busy['new-arrivals']}
            onTest={() => fireCampaign('new-arrivals', 'New arrivals (TEST)', { test: true })}
            onFire={() => fireCampaign('new-arrivals', 'New arrivals')}
          />
          <CampaignCard
            kind="consign-pitch"
            label="Consign with us"
            sub="Sales-acquisition pitch"
            icon={<Send className="w-4 h-4" />}
            busy={busy['consign-pitch']}
            onTest={() => fireCampaign('consign-pitch', 'Consign pitch (TEST)', { test: true })}
            onFire={() => fireCampaign('consign-pitch', 'Consign pitch')}
          />
        </div>
      </section>

      {/* Run history with analytics */}
      {history.length > 0 && (
        <section className="border border-border p-6 space-y-3">
          <h2 className="text-sm uppercase tracking-[0.25em] text-muted-foreground font-sans">
            Recent campaigns
          </h2>
          <table className="w-full text-sm font-sans">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-muted-foreground text-left">
                <th className="py-2">When</th>
                <th>Campaign</th>
                <th>Sent</th>
                <th>Opens</th>
                <th>Clicks</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {history.map((r) => {
                const a = r.analytics;
                return (
                  <tr key={r.id} className="border-t border-border" data-testid={`campaign-history-${r.id}`}>
                    <td className="py-2 text-muted-foreground">{new Date(r.ran_at).toLocaleString()}</td>
                    <td>{r.kind.replace(/_/g, ' ')}</td>
                    <td>{a ? a.sent : (r.summary?.sent ?? '—')}</td>
                    <td>
                      {a ? (
                        <span>
                          <strong>{a.unique_opens}</strong>
                          <span className="text-muted-foreground"> · {a.open_rate}%</span>
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      {a ? (
                        <span>
                          <strong>{a.unique_clicks}</strong>
                          <span className="text-muted-foreground"> · {a.click_rate}%</span>
                          {a.total_clicks > a.unique_clicks && (
                            <span className="text-muted-foreground"> ({a.total_clicks} total)</span>
                          )}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="text-right">
                      {(r.summary?.campaign_run_id) && (
                        <button
                          onClick={() => loadRecipients(r.summary.campaign_run_id)}
                          className="text-xs underline text-muted-foreground hover:text-foreground"
                          data-testid={`campaign-detail-${r.id}`}
                        >
                          View
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {recipientsForRun && (
            <div className="mt-4 pt-4 border-t border-border" data-testid="campaign-recipients-panel">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground">
                  Recipients ({recipientsForRun.count}) — sorted by click count
                </h3>
                <button
                  onClick={() => setRecipientsForRun(null)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                  data-testid="campaign-recipients-close"
                >
                  Close
                </button>
              </div>
              <table className="w-full text-xs font-sans">
                <thead>
                  <tr className="text-muted-foreground text-left">
                    <th className="py-1">Email</th>
                    <th>Sent</th>
                    <th>Opened</th>
                    <th>Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {recipientsForRun.recipients.slice(0, 50).map((r, idx) => (
                    <tr key={`${r.subscriber_id}-${idx}`} className="border-t border-border">
                      <td className="py-1">{r.email}</td>
                      <td className="text-muted-foreground">{(r.sent_at || '').slice(0, 10)}</td>
                      <td className={r.opened_at ? '' : 'text-muted-foreground'}>
                        {r.opened_at ? '✓' : '—'}
                      </td>
                      <td className={r.click_count > 0 ? 'font-semibold' : 'text-muted-foreground'}>
                        {r.click_count || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Subscriber preview */}
      <section className="border border-border p-6 space-y-3">
        <h2 className="text-sm uppercase tracking-[0.25em] text-muted-foreground font-sans">
          Subscribers ({active.toLocaleString()} active)
        </h2>
        <table className="w-full text-sm font-sans">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-muted-foreground text-left">
              <th className="py-2">Email</th><th>Name</th><th>Joined</th><th></th>
            </tr>
          </thead>
          <tbody>
            {subscribers.map((s) => (
              <tr key={s.id} className="border-t border-border" data-testid={`sub-row-${s.id}`}>
                <td className="py-2">{s.email}</td>
                <td>{[s.first_name, s.last_name].filter(Boolean).join(' ') || '—'}</td>
                <td className="text-xs text-muted-foreground">{(s.created_at || '').slice(0, 10)}</td>
                <td className="text-right">
                  <button onClick={() => deleteSub(s.id)} aria-label="Remove"
                    className="text-muted-foreground hover:text-red-600"
                    data-testid={`sub-delete-${s.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {subscribers.length === 0 && (
              <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">
                No subscribers yet — upload a file to get started.
              </td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
};

const CampaignCard = ({ kind, label, sub, icon, busy, onFire, onTest }) => (
  <div className="border border-border p-4 space-y-3" data-testid={`campaign-card-${kind}`}>
    <div className="flex items-center gap-2 text-sm font-sans">
      {icon}<span>{label}</span>
    </div>
    <p className="text-xs text-muted-foreground">{sub}</p>
    <div className="flex gap-2">
      {onTest && (
        <button
          onClick={onTest} disabled={busy}
          className="px-3 py-1.5 border border-border text-xs uppercase tracking-wider hover:bg-muted/30 disabled:opacity-50"
          data-testid={`campaign-test-${kind}`}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Test'}
        </button>
      )}
      <button
        onClick={onFire} disabled={busy}
        className="px-3 py-1.5 bg-foreground text-background text-xs uppercase tracking-wider hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
        data-testid={`campaign-send-${kind}`}
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Send'}
      </button>
    </div>
  </div>
);

export default SubscribersManager;
